/**
 * TS port of {@code IamCelValidator} —
 * {@link file://backend/src/main/java/com/irian/iam/ruleengine/cel/IamCelValidator.java}
 *
 * <p>Walks the parsed CEL AST and enforces the IAM-specific strictness
 * checks that CEL's own type system does not express. Issue 982 case C —
 * the validator rejects attribute keys that are not registered in the
 * schema's FieldNameProvider for the owning entity type.
 *
 * <p>Without this validator on the frontend, every {@code identity.attributes.X}
 * reference would be accepted at parse time by @bufbuild/cel (which treats
 * map field access as dynamic), and the case-C contract would silently
 * leak into evaluation.
 */

import type {ParsedExpr} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'
import type {Expr} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'
import type {IamRuleExpressionSchema} from './IamRuleExpressionSchema'

interface ValidatorContext {
    readonly knownMapKeys: ReadonlyMap<string, ReadonlySet<string>>
}

export class IamCelValidator {
    private readonly schema: IamRuleExpressionSchema

    constructor(schema: IamRuleExpressionSchema) {
        this.schema = schema
    }

    /**
     * Walk the parsed CEL AST and return the list of validation errors. An
     * empty list means the expression passed every IAM-specific strictness
     * check. Mirrors the Java signature.
     */
    validate(parsed: ParsedExpr): string[] {
        const errors: string[] = []
        const ctx: ValidatorContext = {knownMapKeys: this.buildKnownMapKeys()}
        const root = parsed.expr
        if (root) walk(root, errors, ctx)
        return errors
    }

    private buildKnownMapKeys(): ReadonlyMap<string, ReadonlySet<string>> {
        const out = new Map<string, ReadonlySet<string>>()
        const registry = this.schema.registry()
        for (const path of this.schema.attributesPaths()) {
            const provider = registry.fieldNameProvider(path)
            if (provider === null) continue
            const keys = new Set<string>()
            for (const s of provider.getFieldNames('')) keys.add(s.name)
            out.set(path, keys)
        }
        return out
    }
}

function walk(expr: Expr, errors: string[], ctx: ValidatorContext): void {
    const kind = expr.exprKind
    if (!kind) return
    switch (kind.case) {
        case 'selectExpr':
            walkSelect(expr, errors, ctx)
            break
        case 'callExpr':
            walkCall(expr, errors, ctx)
            break
        case 'listExpr':
            for (const e of kind.value.elements ?? []) walk(e, errors, ctx)
            break
        case 'comprehensionExpr': {
            const c = kind.value
            if (c.iterRange) walk(c.iterRange, errors, ctx)
            if (c.loopStep) walk(c.loopStep, errors, ctx)
            if (c.result) walk(c.result, errors, ctx)
            break
        }
        default:
            // Leaf or unsupported — nothing to validate.
            break
    }
}

function walkSelect(expr: Expr, errors: string[], ctx: ValidatorContext): void {
    const sel = (expr.exprKind?.case === 'selectExpr') ? expr.exprKind.value : null
    if (!sel || !sel.operand) return
    const operandPath = stringPath(sel.operand)
    const validKeys = operandPath !== null ? ctx.knownMapKeys.get(operandPath) : null
    if (validKeys && !validKeys.has(sel.field)) {
        // eslint-disable-next-line i18next/no-literal-string -- validator diagnostic, not user-facing prose
        errors.push(`unknown attribute key '${sel.field}' on ${operandPath}`)
    }
    walk(sel.operand, errors, ctx)
}

function walkCall(expr: Expr, errors: string[], ctx: ValidatorContext): void {
    const call = (expr.exprKind?.case === 'callExpr') ? expr.exprKind.value : null
    if (!call) return
    if (call.target) walk(call.target, errors, ctx)
    for (const arg of call.args ?? []) walk(arg, errors, ctx)
}

function stringPath(expr: Expr): string | null {
    const kind = expr.exprKind
    if (!kind) return null
    if (kind.case === 'identExpr') return kind.value.name
    if (kind.case === 'selectExpr') {
        const operand = kind.value.operand
        if (!operand) return null
        const parent = stringPath(operand)
        return parent === null ? null : `${parent}.${kind.value.field}`
    }
    return null
}
